'use client'
import { useQuery } from '@tanstack/react-query'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import React from 'react'
import { createBreakpoint } from 'react-use'
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselDots,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/src/components/ui/carousel'
import { trendingTokensQuery } from '@/src/queries/keys/tokens'
import { Skeleton } from '../ui/skeleton'
import { TrendingCard } from './trending-card'

// Create a breakpoint hook with mobile and desktop breakpoints
// The carousel needs 835px to show fully with padding container
const useBreakpoint = createBreakpoint({ mobile: 0, desktop: 835 })

// Helper function to chunk array into groups of specified size
function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize))
  }
  return chunks
}

// Skeleton component for loading state
function TrendingSkeleton() {
  return <Skeleton className='h-[148px] w-[250px] rounded-2xl' />
}

// Loading skeleton component for the entire trending section
function TrendingSectionSkeleton() {
  return (
    <div className='group relative'>
      <div className='max-w-4xl mx-auto'>
        <div className='flex flex-row w-full items-center justify-between py-4'>
          <h2 className='text-primary text-xl font-semibold'>Trending</h2>
        </div>
      </div>

      {/* Desktop skeleton - shown above 835px */}
      <div className='hidden min-[835px]:block max-w-4xl mx-auto mt-2'>
        <div className='grid grid-cols-3 grid-rows-2 gap-2 max-w-4xl'>
          {Array.from({ length: 6 }).map((_, index) => (
            <TrendingSkeleton key={`desktop-skeleton-${index}`} />
          ))}
        </div>
      </div>

      {/* Mobile skeleton - shown below 835px */}
      <div className='block min-[835px]:hidden w-[calc(100vw+1.25rem)] md:w-[calc(100vw+2rem)] xl:w-[calc(100vw+2.5rem)] relative -left-5 md:-left-8 xl:-left-10 overflow-hidden'>
        <div className='flex gap-2 pl-5 md:pl-8 xl:pl-10'>
          {Array.from({ length: 6 }).map((_, index) => (
            <TrendingSkeleton key={`mobile-skeleton-${index}`} />
          ))}
        </div>
      </div>
    </div>
  )
}

export function TrendingSection() {
  const {
    data: trendingTokens,
    isSuccess,
    isLoading,
  } = useQuery(trendingTokensQuery())
  const breakpoint = useBreakpoint()
  const isDesktop = breakpoint === 'desktop'
  const [api, setApi] = React.useState<CarouselApi>()

  // Show skeleton when loading
  if (isLoading) {
    return <TrendingSectionSkeleton />
  }

  if (!isSuccess || !trendingTokens) return null

  // For desktop: chunk tokens into groups of 6
  // For mobile: each token is its own "chunk"
  const items = isDesktop
    ? chunkArray(trendingTokens, 6)
    : trendingTokens.map((token) => [token])

  return (
    <div className={isDesktop ? 'group relative' : 'group'}>
      <div className='max-w-4xl mx-auto'>
        <div className='flex flex-row w-full items-center justify-between py-4'>
          <h2 className='text-primary text-xl font-semibold'>Trending</h2>
          {isDesktop && <CarouselDots api={api} />}
        </div>
      </div>

      <div
        className={
          !isDesktop
            ? 'overflow-hidden -mx-page-gutter'
            : 'max-w-4xl mx-auto mt-1'
        }
      >
        <Carousel
          key={`carousel-${isDesktop ? 'desktop' : 'mobile'}`} // Force re-render on screen size change
          setApi={setApi}
          opts={{
            align: 'start',
            slidesToScroll: 'auto',
            skipSnaps: true,
            dragFree: !isDesktop, // Disable drag free for desktop to ensure full page snapping
          }}
          plugins={[
            WheelGesturesPlugin({
              forceWheelAxis: 'x',
            }),
          ]}
          className='relative w-full'
        >
          <CarouselContent
            className={isDesktop ? '-ml-4 p-1' : '-ml-2 px-5 mr-5 py-1'}
          >
            {items.map((chunk, chunkIndex) => (
              <CarouselItem
                key={`chunk-${chunkIndex}`}
                className={
                  isDesktop ? 'basis-full ml-0 pl-4' : 'basis-[250px] ml-2'
                }
              >
                {isDesktop ? (
                  // Desktop: 2 rows of 3 cards
                  <div className='grid grid-cols-3 grid-rows-2 gap-2 max-w-4xl p-1 overflow-hidden -m-1'>
                    {chunk.map((token) => (
                      <TrendingCard key={token.ticker} token={token} />
                    ))}
                    {/* Fill empty spots if chunk has less than 6 items */}
                    {Array.from({ length: 6 - chunk.length }).map(
                      (_, emptyIndex) => (
                        <div
                          key={`empty-${chunkIndex}-${emptyIndex}`}
                          className='invisible'
                        />
                      ),
                    )}
                  </div>
                ) : (
                  // Mobile: single card
                  <TrendingCard token={chunk[0]} />
                )}
              </CarouselItem>
            ))}
          </CarouselContent>
          {isDesktop && (
            <>
              <CarouselPrevious />
              <CarouselNext />
            </>
          )}
          {!isDesktop && <CarouselDots api={api} className='mt-4' />}
        </Carousel>
      </div>
    </div>
  )
}
